import { useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import {
  useLazyDownloadFileQuery,
  useStartTopFolderZipMutation,
  useStartTopFolderZipBatchMutation,
  useStartFolderZipStreamMutation,
  useLazyGetTopFolderZipDownloadQuery,
} from '../features/fileSystem/filesStorage';
import { useAppDispatch, useAppSelector } from '../features/store';
import type { RootState } from '../features/store';
import { pushNotification, removeNotification } from '../features/notificationSlice';
import {
  startTopFolderZipJob,
  setTopFolderZipJobId,
  setTopFolderZipReady,
  clearTopFolderZipReady,
  removeTopFolderZipJob,
} from '../features/topFolderZipSlice';
import type { FileItem, TopFolderZipAsyncStartResponse } from "../types/interfaces/filesStorage";
import { downloadQueueItemKey } from "../utils/topFolderDownload";
import { API_URL } from "config";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";

interface DownloadState {
  downloadingItems: Set<string>;
  downloadProgress: Record<string, number | undefined>;
  downloadErrors: Record<string, string | undefined>;
}

interface DownloadResult {
  success: boolean;
  message: string;
  error?: string;
  notificationSeverity?: "success" | "info" | "error";
  meta?: { topFolderZip: "job_started" };
}

export type TopFolderZipBatchGenerateSummary = {
  started: number;
  already: number;
  failed: number;
};

export type UseDownloadManagerOptions = {
  blockTopFolderZipWhileFolderSyncing?: boolean;
};

export const useDownloadManager = (options?: UseDownloadManagerOptions) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const topZipJobs = useAppSelector((s) => s.topFolderZip.jobs);
  const browserZipJobs = useAppSelector((s) => s.browserZip.jobs);
  const blockTopFolderZipWhileFolderSyncing =
    options?.blockTopFolderZipWhileFolderSyncing === true;
  const [state, setState] = useState<DownloadState>({
    downloadingItems: new Set(),
    downloadProgress: {},
    downloadErrors: {},
  });

  const [triggerDownloadFile] = useLazyDownloadFileQuery();
  const [startTopFolderZip] = useStartTopFolderZipMutation();
  const [startTopFolderZipBatch] = useStartTopFolderZipBatchMutation();
  const [startFolderZipStream] = useStartFolderZipStreamMutation();
  const [triggerTopZipDownload] = useLazyGetTopFolderZipDownloadQuery();

  const getItemKey = (item: FileItem) => downloadQueueItemKey(item);

  const isDownloading = (item: FileItem) => {
    const itemKey = getItemKey(item);
    return (
      state.downloadingItems.has(itemKey) ||
      itemKey in topZipJobs ||
      itemKey in browserZipJobs
    );
  };

  const getDownloadProgress = (item: FileItem) => {
    const itemKey = getItemKey(item);
    if (itemKey in browserZipJobs) return undefined;
    const localOrTop =
      state.downloadProgress[itemKey] ?? topZipJobs[itemKey]?.progress ?? 0;
    return localOrTop;
  };

  const downloadingItems = useMemo(() => {
    const s = new Set(state.downloadingItems);
    for (const k of Object.keys(topZipJobs)) {
      s.add(k);
    }
    for (const k of Object.keys(browserZipJobs)) {
      s.add(k);
    }
    return s;
  }, [state.downloadingItems, topZipJobs, browserZipJobs]);

  const downloadProgress = useMemo(() => {
    const m: Record<string, number | undefined> = { ...state.downloadProgress };
    for (const [k, v] of Object.entries(topZipJobs)) {
      m[k] = v.progress;
    }
    for (const k of Object.keys(browserZipJobs)) {
      m[k] = undefined;
    }
    return m;
  }, [state.downloadProgress, topZipJobs, browserZipJobs]);

  const getDownloadError = (item: FileItem) => state.downloadErrors[getItemKey(item)];

  const setDownloading = (item: FileItem, downloading: boolean) => {
    const itemKey = getItemKey(item);
    setState(prev => {
      const newDownloadingItems = new Set(prev.downloadingItems);
      if (downloading) {
        newDownloadingItems.add(itemKey);
      } else {
        newDownloadingItems.delete(itemKey);
      }

      return {
        ...prev,
        downloadingItems: newDownloadingItems,
        downloadProgress: downloading
          ? { ...prev.downloadProgress, [itemKey]: 0 }
          : { ...prev.downloadProgress, [itemKey]: undefined },
        downloadErrors: downloading
          ? { ...prev.downloadErrors, [itemKey]: undefined }
          : { ...prev.downloadErrors, [itemKey]: undefined },
      };
    });
  };

  const setDownloadProgress = (item: FileItem, progress: number) => {
    const itemKey = getItemKey(item);
    setState(prev => ({
      ...prev,
      downloadProgress: { ...prev.downloadProgress, [itemKey]: progress },
    }));
  };

  const setDownloadError = (item: FileItem, error: string) => {
    const itemKey = getItemKey(item);
    setState(prev => ({
      ...prev,
      downloadErrors: { ...prev.downloadErrors, [itemKey]: error },
    }));
  };

  const triggerDownload = (item: FileItem, filename: string) => {
    setDownloadProgress(item, 100);
    setTimeout(() => {
      setDownloading(item, false);
    }, 500);
  };

  const downloadFile = async (item: FileItem): Promise<DownloadResult> => {
    if (isDownloading(item)) {
      return { success: false, message: 'Download already in progress' };
    }

    setDownloading(item, true);
    setDownloadProgress(item, 10);

    let currentProgress = 10;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(currentProgress + 10, 90);
      setDownloadProgress(item, currentProgress);
    }, 200);

    try {
      const response = await triggerDownloadFile({
        source: item.fsource,
        fileName: item.fname,
      }).unwrap();

      clearInterval(progressInterval);
      setDownloadProgress(item, 95);

      if (response && response.file_urls && response.file_urls.length > 0) {
        const link = document.createElement("a");
        link.href = response.file_urls[0];
        link.download = item.fname;
        document.body.appendChild(link);
        link.click();
        link.remove();

        triggerDownload(item, item.fname);
        return {
          success: true,
          message: `File "${item.fname}" downloaded successfully`
        };
      } else {
        const error = 'No download URL available';
        setDownloadError(item, error);
        setDownloading(item, false);
        return { success: false, message: error };
      }
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      setDownloadError(item, errorMessage);
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

  const downloadArchive = async (item: FileItem): Promise<DownloadResult> => {
    if (isDownloading(item)) {
      return { success: false, message: 'Download already in progress' };
    }

    setDownloading(item, true);
    try {
      const started = await startFolderZipStream({
        source: item.fsource,
      }).unwrap();
      const base = API_URL.replace(/\/$/, "");
      const url = `${base}/web/files/top-folder-zip/stream/${encodeURIComponent(started.stream_token)}/`;
      startDownloadViaHiddenFrame(url);
      setDownloading(item, false);
      return {
        success: true,
        message: `ZIP "${item.fname}" download started.`,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "ZIP download failed";
      dispatch(
        pushNotification({
          message: errorMessage,
          severity: "error",
        }),
      );
      setDownloading(item, false);
      return {
        success: false,
        message: errorMessage,
        error: errorMessage,
      };
    }
  };


  const downloadTopFolder = async (item: FileItem): Promise<DownloadResult> => {
    if (blockTopFolderZipWhileFolderSyncing) {
      return {
        success: false,
        message:
          "This folder is still syncing. Wait until sync finishes before using ZIP download.",
        notificationSeverity: "info",
      };
    }
    const itemKey = getItemKey(item);
    if (itemKey in store.getState().topFolderZip.jobs) {
      return { success: false, message: 'Download already in progress' };
    }
    if (itemKey in store.getState().browserZip.jobs) {
      return { success: false, message: 'Download already in progress' };
    }

    const readyEntry = store.getState().topFolderZip.ready[itemKey];
    if (readyEntry?.jobId) {
      setDownloading(item, true);
      try {
        const dl = await triggerTopZipDownload(readyEntry.jobId).unwrap();
        startDownloadViaHiddenFrame(dl.download_url);
        dispatch(clearTopFolderZipReady({ itemKey }));
        dispatch(removeNotification(`tfz-ready-${itemKey}`));
        setDownloading(item, false);
        return {
          success: true,
          message: `ZIP "${item.fname}" download started.`,
        };
      } catch {
        dispatch(
          pushNotification({
            message: "Could not start download. Try again.",
            severity: "error",
          }),
        );
        setDownloading(item, false);
        return {
          success: false,
          message: "Could not start download.",
          error: "Could not start download.",
        };
      }
    }

    dispatch(clearTopFolderZipReady({ itemKey }));
    dispatch(removeNotification(`tfz-ready-${itemKey}`));

    setDownloading(item, true);
    try {
      const started = await startTopFolderZip({ source: item.fsource }).unwrap();

      const jobStart = started as TopFolderZipAsyncStartResponse;

      dispatch(startTopFolderZipJob({ itemKey, label: item.fname }));
      dispatch(setTopFolderZipJobId({ itemKey, jobId: jobStart.job_id }));
      setDownloading(item, false);
      return {
        success: true,
        message: `ZIP for "${item.fname}" is generating…`,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "ZIP job failed";
      dispatch(
        pushNotification({
          message: errorMessage,
          severity: "error",
        }),
      );
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

  const generateTopFolderZip = async (
    item: FileItem,
    options?: { quiet?: boolean },
  ): Promise<DownloadResult> => {
    if (blockTopFolderZipWhileFolderSyncing) {
      if (!options?.quiet) {
        dispatch(
          pushNotification({
            message:
              "Wait until folder sync finishes before generating top-folder ZIPs.",
            severity: "info",
          }),
        );
      }
      return {
        success: false,
        message: "Folder is still syncing.",
        notificationSeverity: "info",
      };
    }
    const itemKey = getItemKey(item);
    if (itemKey in store.getState().topFolderZip.jobs) {
      return {
        success: false,
        message: "ZIP generation already in progress for this folder.",
      };
    }
    dispatch(clearTopFolderZipReady({ itemKey }));
    dispatch(removeNotification(`tfz-ready-${itemKey}`));
    setDownloading(item, true);
    try {
      const started = await startTopFolderZip({ source: item.fsource }).unwrap();
      const jobStart = started as TopFolderZipAsyncStartResponse;

      dispatch(startTopFolderZipJob({ itemKey, label: item.fname }));
      dispatch(setTopFolderZipJobId({ itemKey, jobId: jobStart.job_id }));
      setDownloading(item, false);
      return {
        success: true,
        message: `ZIP generation started for "${item.fname}".`,
        meta: { topFolderZip: "job_started" },
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "ZIP job failed";
      if (!options?.quiet) {
        dispatch(
          pushNotification({
            message: errorMessage,
            severity: "error",
          }),
        );
      }
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

  const generateTopFolderZipBatch = async (
    items: FileItem[],
    options?: { quiet?: boolean },
  ): Promise<TopFolderZipBatchGenerateSummary> => {
    const empty = { started: 0, already: 0, failed: 0 };
    if (items.length === 0) {
      return empty;
    }
    if (blockTopFolderZipWhileFolderSyncing) {
      if (!options?.quiet) {
        dispatch(
          pushNotification({
            message:
              "Wait until folder sync finishes before generating top-folder ZIPs.",
            severity: "info",
          }),
        );
      }
      return empty;
    }

    const byKey = new Map<string, FileItem>();
    for (const item of items) {
      const k = getItemKey(item);
      if (!byKey.has(k)) {
        byKey.set(k, item);
      }
    }

    const jobs = store.getState().topFolderZip.jobs;
    const toStart = Array.from(byKey.values()).filter(
      (item) => !(getItemKey(item) in jobs),
    );
    if (toStart.length === 0) {
      return empty;
    }

    for (const item of toStart) {
      const itemKey = getItemKey(item);
      dispatch(clearTopFolderZipReady({ itemKey }));
      dispatch(removeNotification(`tfz-ready-${itemKey}`));
      dispatch(startTopFolderZipJob({ itemKey, label: item.fname }));
    }

    const counts = { started: 0, already: 0, failed: 0 };

    try {
      const { results } = await startTopFolderZipBatch({
        sources: toStart.map((i) => i.fsource),
      }).unwrap();

      const bySource = new Map(results.map((r) => [r.source, r] as const));

      for (const item of toStart) {
        const row = bySource.get(item.fsource);
        const itemKey = getItemKey(item);
        if (!row || row.ok === false) {
          counts.failed += 1;
          dispatch(removeTopFolderZipJob({ itemKey }));
          continue;
        }
        if (row.status === "completed") {
          counts.already += 1;
          dispatch(
            setTopFolderZipReady({
              itemKey,
              jobId: row.job_id,
              label: item.fname,
            }),
          );
          dispatch(removeTopFolderZipJob({ itemKey }));
        } else {
          counts.started += 1;
          dispatch(setTopFolderZipJobId({ itemKey, jobId: row.job_id }));
        }
      }

      return counts;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "ZIP batch failed";
      if (!options?.quiet) {
        dispatch(
          pushNotification({
            message: errorMessage,
            severity: "error",
          }),
        );
      }
      for (const item of toStart) {
        dispatch(removeTopFolderZipJob({ itemKey: getItemKey(item) }));
      }
      return { started: 0, already: 0, failed: toStart.length };
    }
  };

  const downloadItem = async (item: FileItem): Promise<DownloadResult> => {
    if (item.ftype === "folder") {
      return downloadArchive(item);
    } else {
      return downloadFile(item);
    }
  };

  return {
    downloadingItems,
    downloadProgress,
    downloadErrors: state.downloadErrors,

    isDownloading,
    getDownloadProgress,
    getDownloadError,
    downloadItem,
    downloadFile,
    downloadArchive,
    downloadTopFolder,
    generateTopFolderZip,
    generateTopFolderZipBatch,
  };
};
